import { Save as SaveIcon } from "@mui/icons-material";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ptBR } from "date-fns/locale";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import authService from "../services/auth";
import usuarioService from "../services/usuario";
import { ESTADOS_BRASIL } from "../utils/constants";
import {
  formatCEP,
  formatCpfCnpj,
  formatPhone,
  parseDateWithoutTimezone,
} from "../utils/formatters";
import { toISODate } from "../utils/utils";
import CharCounter from "../components/CharCounter";
import RepresentadosManager from "../components/RepresentadosManager";
import { isConsultor } from "../utils/obterNomeExibicaoUsuario";

const ABA_MEUS_DADOS = 0;
const ABA_REPRESENTADOS = 1;

const CHAR_LIMITS = {
  nome: 150,
  sobrenome: 150,
  email: 50,
  telefone: 15,
  rua: 100,
  numero: 10,
  municipio: 50,
  cep: 9,
  profissao: 50,
  empresa: 50,
  cargo: 50,
};

function AlterarDados() {
  const navigate = useNavigate();
  const hasCalledAPI = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [, setError] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [errors, setErrors] = useState({});
  const [semNumero, setSemNumero] = useState(false);
  const [errorsDeValidacao, setErrorsDeValidacao] = useState([]);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [emailOriginal, setEmailOriginal] = useState("");
  const [abaAtiva, setAbaAtiva] = useState(ABA_MEUS_DADOS);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [formData, setFormData] = useState({
    nome: "",
    sobrenome: "",
    email: "",
    telefone: "",
    data_nascimento: "",
    tipoUsuario: "",
    endereco: { rua: "", municipio: "", estado: "", cep: "", numero: "" },
    interessado: { profissao: "", empresa: "", cargo: "" },
  });

  const handleSemNumeroChange = (e) => {
    const checked = e.target.checked;
    setSemNumero(checked);

    setFormData((prev) => ({
      ...prev,
      endereco: {
        ...prev.endereco,
        numero: checked ? null : "",
      },
    }));
  };

  useEffect(() => {
    if (!hasCalledAPI.current) {
      hasCalledAPI.current = true;
      carregarDados();
    }
  }, []);

  const carregarDados = async () => {
    try {
      const dados = await usuarioService.obterDados();

      const dadosFormatados = {
        ...dados,
        data_nascimento: parseDateWithoutTimezone(dados.data_Nascimento),
        telefone: dados.telefone ? formatPhone(dados.telefone) : "",
        tipoUsuario: dados.tipoUsuario || 0,
        endereco: {
          rua: dados.endereco?.rua || "",
          numero: dados.endereco?.numero || "",
          municipio: dados.endereco?.municipio || "",
          estado: dados.endereco?.estado || "",
          cep: dados.endereco?.cep ? formatCEP(dados.endereco.cep) : "",
        },
        interessado: {
          profissao: dados.interessado?.profissao || "",
          empresa: dados.interessado?.empresa || "",
          cargo: dados.interessado?.cargo || "",
        },
      };
      delete dadosFormatados.data_Nascimento;

      setFormData(dadosFormatados);
      setEmailOriginal((dados.email || "").trim().toLowerCase());
      setLoading(false);
      setSemNumero(!dados.endereco?.numero);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Erro ao carregar dados do usuário.",
        severity: "error",
      });
      setLoading(false);
    }
  };

  const buscarCep = async (cepFormatado) => {
    const digits = cepFormatado.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setFormData((prev) => ({
          ...prev,
          endereco: {
            ...prev.endereco,
            rua: data.logradouro || prev.endereco.rua,
            municipio: data.localidade || prev.endereco.municipio,
            estado: data.uf || prev.endereco.estado,
          },
        }));
      }
    } catch {
    } finally {
      setCepLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    switch (name) {
      case "cpf":
        formattedValue = formatCpfCnpj(value);
        break;
      case "telefone":
        formattedValue = formatPhone(value);
        break;
      case "cep":
        formattedValue = formatCEP(value);
        buscarCep(formattedValue);
        break;
      default:
        formattedValue = value;
    }

    if (["cep", "rua", "municipio", "estado", "numero"].includes(name)) {
      setFormData((prev) => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          [name === "municipio" ? "municipio" : name]: formattedValue,
        },
      }));
    } else if (["profissao", "empresa", "cargo"].includes(name)) {
      setFormData((prev) => ({
        ...prev,
        interessado: {
          ...prev.interessado,
          [name]: formattedValue,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setErrorsDeValidacao([]);

    try {
      if (toISODate(formData.data_nascimento) === null) {
        setErrorsDeValidacao(["Data de nascimento inválida."]);
        setOpenErrorModal(true);
        setLoading(false);
        return;
      }

      const dataToSubmit = {
        ...formData,
        data_nascimento: toISODate(formData.data_nascimento),
      };

      await usuarioService.atualizarDados(dataToSubmit);

      const emailAlterado =
        (formData.email || "").trim().toLowerCase() !== emailOriginal;

      if (emailAlterado) {
        setSnackbar({
          open: true,
          message:
            "Dados atualizados. Como o email foi alterado, voce sera deslogado para confirmar o novo email.",
          severity: "warning",
        });

        setTimeout(async () => {
          await authService.logout();
          navigate("/login", {
            state: {
              message:
                "Email alterado com sucesso. Confirme o novo email para voltar a acessar o sistema.",
            },
          });
        }, 5000);

        return;
      }

      setSnackbar({
        open: true,
        message: "Dados atualizados com sucesso!",
        severity: "success",
      });
      await carregarDados();
    } catch (error) {
      const validation = error.errors;
      if (validation) {
        const mensagens = Object.values(validation).flat();
        setErrorsDeValidacao(mensagens);
      } else if (error.message) {
        const msg = [error.message];
        setErrorsDeValidacao(msg);
      } else {
        setErrorsDeValidacao(["Erro desconhecido."]);
      }

      setOpenErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  const usuarioConsultor = isConsultor(formData);
  const mostrarRepresentados =
    usuarioConsultor && abaAtiva === ABA_REPRESENTADOS;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" } }}
          >
            Alterar Dados
          </Typography>

          {usuarioConsultor && (
            <Tabs
              value={abaAtiva}
              onChange={(_, novaAba) => setAbaAtiva(novaAba)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
            >
              <Tab
                id="alterar-dados-tab-0"
                aria-controls="alterar-dados-tabpanel-0"
                label="Meus dados"
              />
              <Tab
                id="alterar-dados-tab-1"
                aria-controls="alterar-dados-tabpanel-1"
                label="Representados"
              />
            </Tabs>
          )}

          {mostrarRepresentados && (
            <Box
              role="tabpanel"
              id="alterar-dados-tabpanel-1"
              aria-labelledby="alterar-dados-tab-1"
            >
              <RepresentadosManager />
            </Box>
          )}

          <form
            onSubmit={handleSubmit}
            hidden={mostrarRepresentados}
            id="alterar-dados-tabpanel-0"
            aria-label="Formulário de dados pessoais"
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Dados Pessoais
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.nome }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.nome}
                          maxLength={CHAR_LIMITS.nome}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Sobrenome"
                  name="sobrenome"
                  value={formData.sobrenome}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.sobrenome }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.sobrenome}
                          maxLength={CHAR_LIMITS.sobrenome}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.email }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.email}
                          maxLength={CHAR_LIMITS.email}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Telefone"
                  name="telefone"
                  value={formData.telefone || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.telefone }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={ptBR}
                >
                  <DatePicker
                    label="Data de nascimento"
                    value={formData.data_nascimento || null}
                    onChange={(newValue) =>
                      setFormData({
                        ...formData,
                        data_nascimento: newValue,
                      })
                    }
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        name: "data_nascimento",
                        error: false,
                      },
                    }}
                    disableOpenPicker={false}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: { xs: 1, sm: 0 },
              }}
            >
              <Typography variant="h6" gutterBottom>
                Endereço
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={semNumero}
                    onChange={handleSemNumeroChange}
                    sx={{
                      color: "#205723",
                      "&.Mui-checked": { color: "#205723" },
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                      },
                      borderRadius: "6px",
                    }}
                  />
                }
                label="Sem número"
                sx={{
                  margin: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "15px",
                    color: "#333",
                  },
                }}
              />
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={8} md={9}>
                <TextField
                  required
                  fullWidth
                  label="Rua"
                  name="rua"
                  value={formData.endereco.rua || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.rua }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.endereco.rua}
                          maxLength={CHAR_LIMITS.rua}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  required
                  fullWidth
                  label="Número"
                  name="numero"
                  value={formData.endereco.numero ?? ""}
                  onChange={handleChange}
                  disabled={semNumero}
                  error={!!errors.numero}
                  inputProps={{ maxLength: CHAR_LIMITS.numero }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.endereco.numero}
                          maxLength={CHAR_LIMITS.numero}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Cidade"
                  name="municipio"
                  value={formData.endereco.municipio || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.municipio }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.endereco.municipio}
                          maxLength={CHAR_LIMITS.municipio}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    name="estado"
                    value={formData.endereco.estado || ""}
                    onChange={handleChange}
                    label="Estado"
                  >
                    {ESTADOS_BRASIL.map((estado) => (
                      <MenuItem key={estado.sigla} value={estado.sigla}>
                        {estado.sigla}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  required
                  fullWidth
                  label="CEP"
                  name="cep"
                  value={formData.endereco.cep || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.cep }}
                  InputProps={{
                    endAdornment: cepLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={18} />
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ mb: 2 }}>
              Dados Profissionais
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Profissão"
                  name="profissao"
                  value={formData.interessado.profissao || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.profissao }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.interessado.profissao}
                          maxLength={CHAR_LIMITS.profissao}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Empresa"
                  name="empresa"
                  value={formData.interessado.empresa || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.empresa }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.interessado.empresa}
                          maxLength={CHAR_LIMITS.empresa}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cargo"
                  name="cargo"
                  value={formData.interessado.cargo || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.cargo }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.interessado.cargo}
                          maxLength={CHAR_LIMITS.cargo}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="flex-end"
              spacing={2}
              sx={{ mt: 4 }}
            >
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={<SaveIcon />}
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {saving ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Modal
        open={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300 } }}
      >
        <Fade in={openErrorModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95vw", sm: 600, md: 800 },
              maxWidth: "95vw",
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: { xs: 2, sm: 4 },
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Erro ao registrar usuário:
            </Typography>

            <ul>
              {errorsDeValidacao.map((msg, index) => (
                <li key={index}>{msg}</li>
              ))}
            </ul>

            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
              onClick={() => setOpenErrorModal(false)}
            >
              Fechar
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Container>
  );
}

export default AlterarDados;
